
import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import Navbar from "@/components/Navbar";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import { Form, FormField, FormItem, FormControl } from "@/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

// Form validation schema
const formSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  password: z.string().min(6, "Password must be at least 6 characters"),
});

// Demo account details - using Gmail which is a valid domain
const DEMO_EMAIL = "demo_user@gmail.com";
const DEMO_PASSWORD = "password123";
const DEMO_NAME = "Demo User";

export default function Login() {
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { toast } = useToast();
  const { signIn, user } = useAuth();
  
  // Create form with validation
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });
  
  // Redirect if already logged in
  if (user) {
    return <Navigate to="/dashboard" />;
  }

  const handleSubmit = async (values: z.infer<typeof formSchema>) => {
    const { email, password } = values;
    setIsLoading(true);
    setErrorMessage(null);
    
    try {
      console.log("Attempting login with email:", email);
      
      const { error } = await signIn(email, password);
      
      if (error) {
        console.error("Login error:", error);
        
        if (error.message.includes("Invalid login credentials")) {
          setErrorMessage("Invalid email or password. Please try again or create an account if you don't have one.");
        } else {
          setErrorMessage(error.message || "Failed to login. Please try again.");
        }
        setIsLoading(false);
        return;
      }
      
      toast({
        title: "Success!",
        description: "You've been logged in.",
      });
      
    } catch (error: any) {
      console.error("Login exception:", error);
      setErrorMessage(error.message || "An unexpected error occurred. Please try again.");
      setIsLoading(false);
    }
  };

  const handleDemoLogin = async () => {
    form.setValue("email", DEMO_EMAIL);
    form.setValue("password", DEMO_PASSWORD);
    
    setIsLoading(true);
    setErrorMessage(null);
    
    try {
      console.log("Setting up demo account login...");
      
      // First, try to sign in with the demo account
      let { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
        email: DEMO_EMAIL,
        password: DEMO_PASSWORD
      });
      
      if (signInError) {
        console.log("Demo account doesn't exist yet or error signing in:", signInError);
        
        // Create the demo account if sign in failed
        const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
          email: DEMO_EMAIL,
          password: DEMO_PASSWORD,
          options: {
            data: {
              full_name: DEMO_NAME
            }
          }
        });
        
        console.log("Demo account creation attempt:", signUpData?.user ? "User created" : "Creation failed");
        
        if (signUpError) {
          console.error("Failed to create demo account:", signUpError);
          setErrorMessage("Could not create demo account: " + signUpError.message);
          setIsLoading(false);
          return;
        }
        
        // If we got here, sign in with the newly created account
        const { error: newSignInError } = await signIn(DEMO_EMAIL, DEMO_PASSWORD);
        
        if (newSignInError) {
          console.error("Failed to login with new demo account:", newSignInError);
          setErrorMessage("Failed to login with demo account: " + newSignInError.message);
          setIsLoading(false);
          return;
        }
        
        toast({
          title: "Demo Login Successful!",
          description: "You've been logged in with the demo account.",
        });
      } else {
        // Account exists, we're successful
        console.log("Demo login successful");
        toast({
          title: "Demo Login Successful!",
          description: "You've been logged in with the demo account.",
        });
      }
    } catch (error: any) {
      console.error("Demo login exception:", error);
      setErrorMessage(error.message || "Failed to login with demo account.");
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="container mx-auto py-10 max-w-md">
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">Login</CardTitle>
            <CardDescription>
              Enter your email and password to access your account
            </CardDescription>
          </CardHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)}>
              <CardContent className="space-y-4">
                {errorMessage && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Error</AlertTitle>
                    <AlertDescription>{errorMessage}</AlertDescription>
                  </Alert>
                )}
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <Label htmlFor="email">Email</Label>
                      <FormControl>
                        <Input
                          id="email"
                          placeholder="name@example.com"
                          type="email"
                          {...field}
                          disabled={isLoading}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password">Password</Label>
                    <Link
                      to="/forgot-password"
                      className="text-sm text-primary hover:underline"
                    >
                      Forgot password?
                    </Link>
                  </div>
                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormControl>
                        <Input
                          id="password"
                          type="password"
                          {...field}
                          disabled={isLoading}
                        />
                      </FormControl>
                    )}
                  />
                </div>
              </CardContent>
              <CardFooter className="flex flex-col space-y-4">
                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? "Logging in..." : "Login"}
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  className="w-full"
                  onClick={handleDemoLogin}
                  disabled={isLoading}
                >
                  Try Demo Account
                </Button>
                <div className="text-center text-sm">
                  Don't have an account?{" "}
                  <Link to="/register" className="text-primary hover:underline">
                    Register
                  </Link>
                </div>
              </CardFooter>
            </form>
          </Form>
        </Card>
      </div>
    </div>
  );
}
